import { createSelfHostedCameraTransport } from './selfHostedCameraTransport';
import { cameraStreamCleanupLedger } from './cameraStreamCleanupLedger';
import { canonicalCameraAudiences, readCameraOperationOutcome, readPrivateTracerCapability } from './selfHostedCameraApi';

const messages = {
  CAMERA_STOPPED: 'Camera attempt stopped.',
  CAMERA_INVALID_INPUT: 'Invalid camera action.',
  CAMERA_NATIVE_UNSUPPORTED: 'Self-hosted camera publishing is not supported on this installed platform yet.',
  CAMERA_STORAGE_UNAVAILABLE: 'Durable camera recovery storage is unavailable. Cleanup is unconfirmed; retry after enabling storage.',
  CAMERA_REQUEST_TIMEOUT: 'Camera outcome is unknown. Retry stop.',
  CAMERA_CLEANUP_PENDING: 'Earlier camera cleanup is unconfirmed. Retry stop.',
  CAMERA_FAILED: 'Camera interrupted. Tap Start to try again.',
  REQUEST_ACCOUNT_CHANGED: 'Account changed. Camera stopped.',
};
const failure = code => Object.assign(new Error(messages[code]), { code });
const stopTracks = stream => { for (const track of stream?.getTracks() || []) { try { track.stop(); } catch { /* Exact owned tracks only. */ } } };
const validKey = key => typeof key === 'string' && /^[A-Za-z0-9_-]{8,80}$/.test(key);

// One instance belongs to one authenticated account, above panel/route lifetime.
// The caller must wire account/lifecycle changes. This package does not install it.
export function createSelfHostedCameraController({
  api, authorizedMediaOrigin, cleanupLedger = cameraStreamCleanupLedger,
  mediaDevices = typeof navigator === 'undefined' ? null : navigator.mediaDevices,
  transportFactory = createSelfHostedCameraTransport,
  isNativePlatform = () => typeof window !== 'undefined' && Boolean(window.Capacitor &&
    (typeof window.Capacitor.isNativePlatform !== 'function' || window.Capacitor.isNativePlatform())),
  createOperationKey = () => window.crypto.randomUUID(),
  requestTimeoutMs = 10000, settleTimeoutMs = 2000, pollIntervalMs = 500, liveConfirmAttempts = 20,
} = {}) {
  if (!api?.scope?.identity || !api.scope.ownerId || !cleanupLedger
    || ![requestTimeoutMs, settleTimeoutMs, pollIntervalMs, liveConfirmAttempts].every(n => Number.isFinite(n) && n > 0)
    || requestTimeoutMs > 30000 || settleTimeoutMs > 10000 || pollIntervalMs > 5000
    || !Number.isInteger(liveConfirmAttempts) || liveConfirmAttempts > 60) throw failure('CAMERA_INVALID_INPUT');
  const identity = api.scope.identity, ownerId = api.scope.ownerId;
  let current = null, revision = 0, disposed = false, background = false, recovered = false, disposal;
  const debts = new Map(), pendingWrites = new Set(), listeners = new Set();
  const settled = new Set(), cleanups = new Map(), pendingDischarges = new Set();
  let state = { status: 'off', previewStream: null, audioEnabled: false, session: null,
    panelVisible: false, cleanupPending: false, durability: 'available', error: null, errorCode: null, settings: null };
  const snapshot = () => ({ ...state, session: state.session && { ...state.session, audiences: [...state.session.audiences],
      ...(state.session.live ? { live: { ...state.session.live } } : {}) },
    settings: state.settings && { ...state.settings, default_audiences: [...state.settings.default_audiences] } });
  function emit(patch) {
    state = { ...state, ...patch };
    for (const fn of listeners) { try { fn(snapshot()); } catch { /* Subscriber cannot interrupt local release. */ } }
  }
  const ownerCurrent = () => { try { api.assertCurrent(); return true; } catch { return false; } };
  const active = a => !disposed && !background && !a.closed && current === a && ownerCurrent();
  const check = a => { if (!active(a)) throw failure('CAMERA_STOPPED'); };
  function bounded(promise, ms = requestTimeoutMs, cancellation) {
    let timer;
    const deadline = new Promise((_, reject) => { timer = setTimeout(() => reject(failure('CAMERA_REQUEST_TIMEOUT')), ms); });
    return Promise.race([Promise.resolve(promise), deadline, ...(cancellation ? [cancellation] : [])]).finally(() => clearTimeout(timer));
  }
  const step = async (a, promise) => { const value = await bounded(promise, requestTimeoutMs, a.cancelled); check(a); return value; };
  const hasResponsibility = () => debts.size > 0 || pendingWrites.size > 0 || pendingDischarges.size > 0;
  function discloseDebt() {
    const pending = hasResponsibility();
    emit({ cleanupPending: pending,
      ...(!current && pending ? { status: 'stop_unconfirmed', error: messages.CAMERA_CLEANUP_PENDING, errorCode: 'CAMERA_CLEANUP_PENDING' } : {}),
      ...(!current && !pending && state.status === 'stop_unconfirmed' ? { status: 'off', error: null, errorCode: null } : {}) });
  }
  function readDebts() {
    // Retain exact initiating-owner discoveries even after the consumer times
    // out or loses capture eligibility. A late read never dispatches cleanup.
    return Promise.resolve(cleanupLedger.entries(identity)).then(entries => {
      for (const entry of entries) {
        if (entry.operationId && settled.has(entry.operationId)) continue;
        const key = entry.operationId || entry.sessionId, known = debts.get(key);
        debts.set(key, { ...entry, ...known, sessionId: known?.sessionId || entry.sessionId });
      }
      if (hasResponsibility()) discloseDebt();
      return entries;
    });
  }
  function writeIntent(a) {
    // Register before invoking storage; cancellation releases capture, not this
    // promise's ownership. Both write boundaries remain owned to completion.
    let resolve, reject;
    const writing = new Promise((done, fail) => { resolve = done; reject = fail; });
    pendingWrites.add(writing);
    const finish = (receipt, error) => {
      if (!error && receipt?.durable !== true) error = failure('CAMERA_STORAGE_UNAVAILABLE');
      if (!error && !settled.has(a.entry.operationId)) debts.set(a.entry.operationId, a.entry);
      pendingWrites.delete(writing);
      discloseDebt();
      if (error) reject(error); else resolve(receipt);
    };
    try {
      Promise.resolve(cleanupLedger.owe(identity, { ...a.entry })).then(receipt => finish(receipt), error => finish(null, error));
    } catch (error) { finish(null, error); }
    return writing;
  }
  function release(a) {
    if (!a || a.closed) return;
    a.closed = true;
    a.cancel(failure('CAMERA_STOPPED'));
    for (const remove of a.removers.splice(0)) remove();
    // Invalidate before invoking transport/subscribers; never dereference current
    // after an await to release capture belonging to an obsolete attempt.
    stopTracks(a.stream); a.stream = null;
    try { Promise.resolve(a.transport?.dispose()).catch(() => {}); } catch { /* Local tracks already released. */ }
  }
  function safeSession(s, expectedId) {
    if (!s || typeof s.id !== 'string' || !/^[a-f0-9]{24}$/.test(s.id) || s.ownerId !== ownerId
      || (expectedId && s.id !== expectedId) || typeof s.audio_enabled !== 'boolean'
      || !['ready', 'live', 'ending', 'finalizing', 'ended', 'failed'].includes(s.state)
      || (s.live !== undefined && !(typeof s.live?.day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.live.day)
        && typeof s.live.deviceName === 'string' && s.live.deviceName.length <= 60))) throw failure('CAMERA_FAILED');
    return { id: s.id, ownerId, state: s.state, audiences: canonicalCameraAudiences(s.audiences), audio_enabled: s.audio_enabled,
      ...(s.live ? { live: { day: s.live.day, deviceName: s.live.deviceName } } : {}) };
  }
  async function acceptOutcome(entry, response) {
    if (!entry.operationId || settled.has(entry.operationId) || !ownerCurrent()) return false;
    const knownSession = debts.get(entry.operationId)?.sessionId || entry.sessionId;
    const outcome = readCameraOperationOutcome(response?.outcome, ownerId, entry.operationId, knownSession);
    if (!outcome.accepted) return false;
    const ref = { operationId: entry.operationId, sessionId: outcome.sessionId };
    // No await between the owner/binding checks and starting the exact owner's
    // transaction. Keep debt until its strict completion receipt, even on timeout.
    api.assertCurrent();
    const writing = Promise.resolve(cleanupLedger.discharge(identity, ref));
    pendingDischarges.add(writing);
    try {
      const receipt = await writing;
      api.assertCurrent();
      if (receipt?.durable !== true || receipt.operationId !== ref.operationId
        || receipt.sessionId !== ref.sessionId) throw failure('CAMERA_STORAGE_UNAVAILABLE');
      settled.add(ref.operationId);
      debts.delete(ref.operationId);
      return true;
    } catch (error) {
      emit({ durability: 'degraded' });
      throw error;
    } finally { pendingDischarges.delete(writing); discloseDebt(); }
  }
  function cleanup(entry, ms = settleTimeoutMs) {
    if (!ownerCurrent()) return Promise.resolve();
    const key = entry.operationId || entry.sessionId;
    // A late Start ACK may add a session while cancellation is already pending.
    // Deduplicating settlement must not drop that newly acquired responsibility.
    const stop = entry.sessionId ? Promise.resolve().then(() => { api.assertCurrent(); return api.stop(entry.sessionId); }).catch(() => {}) : Promise.resolve();
    if (cleanups.has(key)) return bounded(cleanups.get(key), ms).catch(() => {});
    const end = Date.now() + ms;
    const remaining = () => Math.max(1, end - Date.now());
    // Stop remains exact-session best effort; neither it nor transport DELETE
    // supplies discharge authority. Late Start sessions are still stopped.
    const work = Promise.resolve().then(async () => {
      if (!entry.operationId || settled.has(entry.operationId)) return;
      let response;
      try {
        api.assertCurrent();
        response = await bounded(api.cancel(entry.operationId), remaining());
      } catch { /* One fresh exact lookup may reconstruct a lost ACK. */ }
      let accepted = false;
      try {
        // Validate separately: a failed storage commit must not trigger a second
        // discharge in this pass, nor any automatic retry loop.
        accepted = readCameraOperationOutcome(response?.outcome, ownerId, entry.operationId,
          debts.get(key)?.sessionId || entry.sessionId).accepted;
      } catch { /* Malformed/missing authority remains debt. */ }
      if (!accepted && ownerCurrent() && Date.now() < end && typeof api.operationOutcome === 'function') {
        api.assertCurrent();
        response = await bounded(api.operationOutcome(entry.operationId), remaining());
      }
      await acceptOutcome(entry, response);
    }).catch(() => {}).then(() => bounded(stop, remaining()).catch(() => {}));
    cleanups.set(key, work);
    work.finally(() => { if (cleanups.get(key) === work) cleanups.delete(key); });
    return bounded(work, ms).catch(() => {});
  }
  function failAttempt(a, code = 'CAMERA_FAILED') {
    const owned = current === a;
    release(a);
    if (owned) {
      current = null; revision++;
      emit({ status: hasResponsibility() ? 'stop_unconfirmed' : 'error', previewStream: null, audioEnabled: false,
        cleanupPending: hasResponsibility(), error: messages[code] || messages.CAMERA_FAILED, errorCode: code });
    }
    if (a.dispatched) void cleanup(a.entry);
  }
  async function openPreview({ audioEnabled = false, deviceId } = {}) {
    if (disposed || background) throw failure('CAMERA_STOPPED');
    api.assertCurrent();
    if (state.settings?.privateTracer && (!state.settings.privateTracer.allowed || audioEnabled)) throw failure('CAMERA_INVALID_INPUT');
    if (isNativePlatform()) throw failure('CAMERA_NATIVE_UNSUPPORTED');
    if (typeof audioEnabled !== 'boolean' || (deviceId !== undefined && typeof deviceId !== 'string')) throw failure('CAMERA_INVALID_INPUT');
    if (current?.starting || ['creating', 'connecting', 'live'].includes(state.status)) throw failure('CAMERA_INVALID_INPUT');
    release(current);
    const a = { closed: false, stream: null, transport: null, removers: [], dispatched: false };
    a.cancelled = new Promise((_, reject) => { a.cancel = reject; }); a.cancelled.catch(() => {});
    current = a; revision++;
    emit({ status: 'requesting_camera', previewStream: null, session: null, panelVisible: true, audioEnabled, error: null, errorCode: null });
    try {
      check(a);
      const acquired = Promise.resolve(mediaDevices.getUserMedia({
        video: deviceId ? { deviceId: { exact: deviceId } } : true, audio: audioEnabled,
      })).then(stream => {
        if (!active(a)) { stopTracks(stream); throw failure('CAMERA_STOPPED'); }
        a.stream = stream;
        return stream;
      });
      const stream = await step(a, acquired);
      const tracks = stream.getTracks();
      if (!tracks.some(t => t.kind === 'video' && t.readyState === 'live')
        || tracks.some(t => t.readyState !== 'live' || !['video', 'audio'].includes(t.kind) || (t.kind === 'audio' && !audioEnabled))) throw failure('CAMERA_FAILED');
      for (const track of tracks) {
        const ended = () => { if (active(a)) failAttempt(a); };
        track.addEventListener('ended', ended); a.removers.push(() => track.removeEventListener('ended', ended));
      }
      a.audioEnabled = audioEnabled;
      emit({ status: 'preview', previewStream: stream });
    } catch (error) { if (current === a && !a.closed) failAttempt(a, error.code === 'REQUEST_ACCOUNT_CHANGED' ? error.code : 'CAMERA_FAILED'); else release(a); }
    return snapshot();
  }
  async function start({ audiences, media, live } = {}) {
    if (disposed || background) throw failure('CAMERA_STOPPED');
    api.assertCurrent();
    const normalized = canonicalCameraAudiences(audiences);
    const a = current;
    if (!a || a.closed || state.status !== 'preview' || a.starting) throw failure('CAMERA_INVALID_INPUT');
    if (hasResponsibility()) throw failure('CAMERA_CLEANUP_PENDING');
    const key = createOperationKey(); if (!validKey(key)) throw failure('CAMERA_INVALID_INPUT');
    a.starting = true; a.entry = { operationId: key };
    emit({ status: 'creating', error: null, errorCode: null });
    try {
      try { await step(a, readDebts()); }
      catch (error) {
        if (active(a)) emit({ durability: 'degraded' });
        throw error.code === 'CAMERA_STOPPED' ? error : failure('CAMERA_STORAGE_UNAVAILABLE');
      }
      if (hasResponsibility()) throw failure('CAMERA_CLEANUP_PENDING');
      try { await step(a, writeIntent(a)); }
      catch (error) {
        if (active(a)) emit({ durability: 'degraded' });
        throw error.code === 'CAMERA_STOPPED' ? error : failure('CAMERA_STORAGE_UNAVAILABLE');
      }
      check(a);
      if (settled.has(key)) throw failure('CAMERA_STOPPED');
      a.dispatched = true;
      emit({ cleanupPending: true });
      check(a); // A subscriber may Stop synchronously.
      const request = Promise.resolve(api.start({ operationKey: key, audiences: normalized, audio_enabled: a.audioEnabled, ...(media ? { media } : {}), ...(live ? { live } : {}) })).then(result => {
        // Own late Start acquisition independently of the caller's deadline.
        const s = safeSession(result?.session);
        a.entry.sessionId = s.id;
        if (!active(a)) void cleanup(a.entry);
        return { session: s, publish: result.publish };
      });
      const result = await step(a, request);
      try {
        const receipt = await step(a, writeIntent(a));
        if (receipt?.durable !== true) throw failure('CAMERA_STORAGE_UNAVAILABLE');
      } catch (error) {
        if (active(a)) emit({ durability: 'degraded' });
        throw error;
      }
      check(a); // The post-ACK storage boundary is an ownership boundary too.
      if (settled.has(key)) throw failure('CAMERA_STOPPED');
      emit({ status: 'connecting', session: result.session }); check(a);
      a.transport = transportFactory({ role: 'publish', authorizedMediaOrigin, ...(result.session.privateTracer || state.settings?.privateTracer ? { recordingVideo: true } : {}) });
      a.removers.push(a.transport.subscribe(s => {
        if (active(a) && ['error', 'stopped'].includes(s.status)) failAttempt(a);
      }));
      await step(a, a.transport.start({ endpoint: result.publish?.whipUrl, token: result.publish?.token,
        stream: a.stream, audioOptIn: a.audioEnabled }));
      for (let n = 0; n < liveConfirmAttempts; n++) {
        if (n) await step(a, new Promise(resolve => setTimeout(resolve, pollIntervalMs)));
        check(a);
        const response = await step(a, api.status(a.entry.sessionId));
        const s = safeSession(response?.session, a.entry.sessionId);
        if (s.state === 'live') { emit({ status: 'live', session: s }); return snapshot(); }
        if (s.state !== 'ready') throw failure('CAMERA_FAILED');
      }
      throw failure('CAMERA_FAILED');
    } catch (error) {
      if (current === a && !a.closed) failAttempt(a, messages[error.code] ? error.code : 'CAMERA_FAILED');
      else { release(a); if (a.dispatched) void cleanup(a.entry); }
    } finally { a.starting = false; }
    return snapshot();
  }
  function stop() {
    const a = current; current = null; revision++;
    release(a);
    const entries = [...debts.values()];
    const pending = hasResponsibility();
    emit({ status: pending ? 'stop_unconfirmed' : 'off', previewStream: null, audioEnabled: false,
      cleanupPending: pending, error: pending ? messages.CAMERA_CLEANUP_PENDING : null, errorCode: pending ? 'CAMERA_CLEANUP_PENDING' : null });
    return bounded(Promise.all(entries.map(e => cleanup(e))), settleTimeoutMs).catch(() => {}).then(snapshot);
  }
  async function refresh() {
    if (disposed) throw failure('CAMERA_STOPPED');
    const rev = revision;
    const settings = await bounded(api.getSettings()); api.assertCurrent();
    const clean = { default_audiences: canonicalCameraAudiences(settings.default_audiences), audio_enabled: settings.audio_enabled };
    const privateTracer = readPrivateTracerCapability(settings.privateTracer);
    if (privateTracer) clean.privateTracer = privateTracer;
    if (typeof clean.audio_enabled !== 'boolean') throw failure('CAMERA_INVALID_INPUT');
    if (!disposed && rev === revision) emit({ settings: clean });
    return snapshot();
  }
  async function recover({ retry = false } = {}) {
    if (disposed || current || (recovered && !retry)) return snapshot();
    recovered = true;
    const rev = revision, end = Date.now() + settleTimeoutMs;
    const eligible = () => !disposed && !current && rev === revision && ownerCurrent();
    try {
      await bounded(readDebts(), settleTimeoutMs);
      if (!eligible()) return snapshot();
      // One pass, no timer/reconnect loop. A total deadline includes the read.
      for (const e of debts.values()) {
        if (!eligible() || Date.now() >= end) break;
        await cleanup(e, Math.max(1, end - Date.now()));
        if (!eligible()) return snapshot();
      }
      if (eligible()) emit({ status: hasResponsibility() ? 'stop_unconfirmed' : 'off', cleanupPending: hasResponsibility(),
        error: hasResponsibility() ? messages.CAMERA_CLEANUP_PENDING : null });
    } catch {
      if (eligible()) emit({ status: 'stop_unconfirmed', cleanupPending: true, durability: 'degraded', error: messages.CAMERA_CLEANUP_PENDING });
    }
    return snapshot();
  }
  function settle() {
    if (disposal) return disposal;
    let finish; disposal = new Promise(resolve => { finish = resolve; });
    // Fence disposal before notifying subscribers or invoking any asynchronous API.
    disposed = true;
    const deadline = Date.now() + settleTimeoutMs;
    const stopping = stop();
    listeners.clear();
    const work = (async () => {
      await stopping;
      await bounded(readDebts(), Math.max(1, deadline - Date.now()));
      await bounded(Promise.allSettled([...pendingWrites, ...pendingDischarges]), Math.max(1, deadline - Date.now()));
      return { localReleased: true, owed: debts.size, confirmed: !hasResponsibility(), timedOut: false };
    })();
    const result = bounded(work, Math.max(1, deadline - Date.now())).catch(() => {
      emit({ status: 'stop_unconfirmed', cleanupPending: true, error: messages.CAMERA_CLEANUP_PENDING, errorCode: 'CAMERA_CLEANUP_PENDING' });
      return { localReleased: true, owed: Math.max(1, debts.size, pendingWrites.size, pendingDischarges.size), confirmed: false, timedOut: true };
    });
    // Reentrant calls share the in-flight settlement, but later calls reassess
    // late storage completion rather than returning a cached certainty.
    result.then(value => { finish(value); disposal = null; });
    return disposal;
  }
  return {
    getState: snapshot,
    subscribe(fn) { if (disposed) return () => {}; listeners.add(fn); return () => listeners.delete(fn); },
    openPreview, start, stop, refresh, recover,
    retryStop: () => current ? stop() : recover({ retry: true }),
    detachPreviewSurface() {
      if (current && ['requesting_camera', 'preview'].includes(state.status)) return stop();
      emit({ panelVisible: false }); return Promise.resolve(snapshot());
    },
    attachPreviewSurface() { if (!disposed) emit({ panelVisible: true }); return snapshot(); },
    handleAppBackground() { background = true; return stop(); },
    handleAppForeground() { background = false; return snapshot(); },
    settleForSignOut: () => settle(), dispose: () => settle(),
  };
}
